import type { WorldId } from '@/config/worlds';
import { COLOR } from '@/config/designTokens';
import PixelIcon from './PixelIcon';

interface PlayerStatusBarProps {
  className?: string;
  mode?: 'home' | 'hud';
  worldId?: WorldId;
  score?: number;
  lives: number;
  coins: number;
  gems: number;
  streak: number;
  canClaimStreak?: boolean;
  nextLifeTimer?: string | null;
  onStreakClick?: () => void;
}

interface StatusChip {
  id: string;
  icon: 'score' | 'life' | 'coin' | 'gem' | 'gift' | 'fire';
  value: number | string;
  label: string;
  tone: 'score' | 'life' | 'coin' | 'gem' | 'streak';
  timer?: string | null;
}

function formatValue(value: number | string): string {
  if (typeof value !== 'number') return value;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return value.toLocaleString();
}

function formatLabelValue(chip: StatusChip): string {
  return `${chip.label}: ${formatValue(chip.value)}`;
}

const iconColorByTone: Record<StatusChip['tone'], string> = {
  score: COLOR.score,
  life: COLOR.lifeLight,
  coin: COLOR.coinLight,
  gem: COLOR.gemLight,
  streak: COLOR.streak,
};

export default function PlayerStatusBar({
  className = '',
  mode = 'home',
  worldId,
  score,
  lives,
  coins,
  gems,
  streak,
  canClaimStreak = false,
  nextLifeTimer,
  onStreakClick,
}: PlayerStatusBarProps) {
  const chips: StatusChip[] = [];

  if (typeof score === 'number') {
    chips.push({ id: 'score', icon: 'score', value: score, label: 'Score', tone: 'score' });
  }

  chips.push(
    { id: 'lives', icon: 'life', value: lives, label: 'Lives', tone: 'life', timer: nextLifeTimer },
    { id: 'coins', icon: 'coin', value: coins, label: 'Coins', tone: 'coin' },
    { id: 'gems', icon: 'gem', value: gems, label: 'Gems', tone: 'gem' },
    { id: 'streak', icon: onStreakClick ? 'gift' : 'fire', value: streak, label: 'Streak', tone: 'streak' },
  );

  const rootClassName = [
    'player-status-bar',
    `player-status-bar--${mode}`,
    worldId ? `player-status-bar--${worldId}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderChip = (chip: StatusChip) => (
    <>
      <span className="player-status-bar__icon">
        <PixelIcon name={chip.icon} size={16} color={iconColorByTone[chip.tone]} />
      </span>
      <span className="player-status-bar__content">
        <span className="player-status-bar__label">{chip.label}</span>
        <span className="player-status-bar__value">{formatValue(chip.value)}</span>
      </span>
      {chip.timer && <span className="player-status-bar__timer">{chip.timer}</span>}
      {chip.id === 'streak' && canClaimStreak && (
        <span className="player-status-bar__badge" aria-label="Daily reward available">
          !
        </span>
      )}
    </>
  );

  return (
    <div className={rootClassName} role="group" aria-label="Player status">
      {chips.map((chip) =>
        chip.id === 'streak' && onStreakClick ? (
          <button
            key={chip.id}
            type="button"
            className="player-status-bar__item player-status-bar__item--interactive"
            onClick={onStreakClick}
            aria-label="Open daily rewards"
            title={formatLabelValue(chip)}
          >
            {renderChip(chip)}
          </button>
        ) : (
          <div key={chip.id} className="player-status-bar__item" title={formatLabelValue(chip)}>
            {renderChip(chip)}
          </div>
        ),
      )}
    </div>
  );
}
